import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { JwtService } from '@app/core/auth';

@Injectable({
  providedIn: 'root'
})
export class AgentService {

  private isAgentSubject = new BehaviorSubject<boolean>(false);
  public isAgent$ = this.isAgentSubject.asObservable();

  private isAdminSubject = new BehaviorSubject<boolean>(false);
  public isAdmin$ = this.isAdminSubject.asObservable();

  constructor(private jwtService: JwtService) {
    this.initializeFromToken();
  }

  /**
   * Subscribe to token changes and derive role from JWT.
   * The token's `role` field is the single source of truth.
   */
  private initializeFromToken(): void {
    this.jwtService.token$.subscribe(decodedToken => {
      if (decodedToken) {
        this.isAgentSubject.next(decodedToken.role === 'agent');
        this.isAdminSubject.next(decodedToken.role === 'admin');
      } else {
        this.isAgentSubject.next(false);
        this.isAdminSubject.next(false);
      }
    });
  }

  public isAgent(): Observable<boolean> {
    return this.isAgent$;
  }

  public isAgentValue(): boolean {
    return this.isAgentSubject.value;
  }

  public isAdmin(): Observable<boolean> {
    return this.isAdmin$;
  }

  public isAdminValue(): boolean {
    return this.isAdminSubject.value;
  }

  public verifyAgentAccess(): Observable<{
    isAgent: boolean;
    hasValidAppId: boolean;
    reason?: string;
  }> {
    const decodedToken = this.jwtService.decodeToken();
    const isAgent = decodedToken?.role === 'agent';

    let reason: string | undefined;
    if (!decodedToken) {
      reason = 'No valid token';
    } else if (decodedToken.role !== 'agent') {
      reason = 'User role is not agent';
    }

    return new Observable(observer => {
      observer.next({ isAgent, hasValidAppId: isAgent, reason });
      observer.complete();
    });
  }

  public getAgentInfo(): Observable<{
    isAgent: boolean;
    email?: string;
    firstname?: string;
    surname?: string;
  }> {
    const decodedToken = this.jwtService.decodeToken();
    const isAgent = decodedToken?.role === 'agent';

    if (isAgent && decodedToken) {
      const nameParts = (decodedToken.name || '').split(' ');
      const firstname = nameParts[0] || '';
      const surname = nameParts.slice(1).join(' ') || '';

      return new Observable(observer => {
        observer.next({
          isAgent: true,
          email: decodedToken.sub,
          firstname,
          surname
        });
        observer.complete();
      });
    }

    return new Observable(observer => {
      observer.next({ isAgent: false });
      observer.complete();
    });
  }
}
